// ─────────────────────────────────────────────────────────
// Off Market Karlshorst — Property Investment Calculator
// Ported from property-calculator_1.html
// Pure functions, German tax-accurate (degressive AfA + §7b Sonder-AfA)
// ─────────────────────────────────────────────────────────

export const fmtEUR = (n, dec = 0) => {
  if (!isFinite(n)) return "–";
  return (
    n.toLocaleString("de-DE", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }) + " €"
  );
};

export const fmtNum = (n, dec = 0) => {
  if (!isFinite(n)) return "–";
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
};

export const fmtPct = (n, dec = 2) => {
  if (!isFinite(n)) return "–";
  return (
    n.toLocaleString("de-DE", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }) + " %"
  );
};

// Small numeric helper
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// ───────────────────────────────────────────────────────────────────────
// Loan amortisation — German simple-proportional convention.
//
// For each period i with `months[i]` months of accrual:
//   annuity_i  = balanceStart × (rate + tilg) × months / 12
//   interest_i = balanceStart × rate          × months / 12
//   principal_i = annuity_i − interest_i (clamped, set to 0 inside grace)
//   balanceEnd = balanceStart − principal_i
//
// `graceYears` is interpreted as full-year periods at the start during
// which only interest is charged. (Partial Y1 still counts as one year
// of grace under this convention.) This matches German exposé tooling.
// ───────────────────────────────────────────────────────────────────────
function amortizeProportional(principal, interestRate, tilgRate, monthsArr, graceYears = 0) {
  let balance = principal;
  const schedule = [];
  for (let i = 0; i < monthsArr.length; i++) {
    const months = monthsArr[i];
    const frac = months / 12;
    const interest = balance * interestRate * frac;
    let principalPaid = 0;
    let payment = 0;
    let isGrace = false;
    if (i < graceYears) {
      payment = interest;
      principalPaid = 0;
      isGrace = true;
    } else {
      const annuity = principal * (interestRate + tilgRate) * frac;
      payment = annuity;
      principalPaid = annuity - interest;
      if (principalPaid > balance) {
        principalPaid = balance;
        payment = interest + principalPaid;
      }
      if (principalPaid < 0) principalPaid = 0;
    }
    balance -= principalPaid;
    schedule.push({ year: i + 1, interest, principalPaid, payment, balanceEnd: balance, isGrace, months });
  }
  return { schedule };
}

// Legacy annual amortiser kept for back-compat with anything outside calculate()
// (no current callers; safe to remove in a later cleanup).
function amortizeAnnuity(principal, interestRate, initialTilgungRate, years, graceYears = 0) {
  const annuity = principal * (interestRate + initialTilgungRate);
  let balance = principal;
  const schedule = [];
  for (let y = 1; y <= years; y++) {
    const interest = balance * interestRate;
    let principalPaid = 0;
    let payment = 0;
    let isGrace = false;
    if (y <= graceYears) {
      payment = interest;
      principalPaid = 0;
      isGrace = true;
    } else {
      payment = annuity;
      principalPaid = annuity - interest;
      if (principalPaid > balance) {
        principalPaid = balance;
        payment = interest + principalPaid;
      }
    }
    balance -= principalPaid;
    schedule.push({ year: y, interest, principalPaid, payment, balanceEnd: balance, isGrace });
  }
  return { annuity, schedule };
}

// Depreciation: degressive 5 % + §7b Sonder-AfA (cap 4.000 €/m²) + furniture linear 10 y.
// `monthsArr` lets partial first/last years scale the period's AfA proportionally.
// `linearRate` — the linear AfA % applied when useDegressive=false. Defaults to 0.03
// (new-build standard). Existing-property purchases use 0.02 (§7 Abs 4 Nr 2a EStG).
function buildDepreciationSchedule(buildingBasis, sqm, useDegressive, useSonder7b, monthsArr, furnitureBasis, linearRate = 0.03) {
  let bookValue = buildingBasis;
  const s7bBase = Math.min(buildingBasis, sqm * 4000);
  const s7bAmountAnnual = useSonder7b ? s7bBase * 0.05 : 0;
  const s7bYears = useSonder7b ? 4 : 0;
  const furnPerYear = furnitureBasis / 10;
  const sched = [];
  for (let i = 0; i < monthsArr.length; i++) {
    const months = monthsArr[i];
    const frac = months / 12;
    let degAfA = 0;
    if (useDegressive) degAfA = bookValue * 0.05 * frac;
    else degAfA = buildingBasis * linearRate * frac;
    const s7b = i < s7bYears ? s7bAmountAnnual * frac : 0;
    const furn = i < 10 ? furnPerYear * frac : 0;
    sched.push({ year: i + 1, degressive: degAfA, sonder7b: s7b, furniture: furn, total: degAfA + s7b + furn });
    if (useDegressive) bookValue -= degAfA + s7b;
    else bookValue -= s7b;
  }
  return sched;
}

// IRR via Newton-Raphson
function irr(cashflows, guess = 0.1) {
  let rate = guess;
  for (let i = 0; i < 100; i++) {
    let npv = 0,
      dnpv = 0;
    for (let t = 0; t < cashflows.length; t++) {
      npv += cashflows[t] / Math.pow(1 + rate, t);
      if (t > 0) dnpv += (-t * cashflows[t]) / Math.pow(1 + rate, t + 1);
    }
    if (Math.abs(npv) < 1e-6) return rate;
    if (dnpv === 0) return NaN;
    rate -= npv / dnpv;
  }
  return rate;
}

export function calculate(inp) {
  // ──────────────────────────────────────────────────────────────────
  // Period model
  // ──────────────────────────────────────────────────────────────────
  // Two timing conventions are supported:
  //
  //  • Default ("12,12,...,12") — N annual periods, every period 12 months.
  //    Used by Off Market Karlshorst to match its exposé matrices.
  //
  //  • German developer "half-year-first" convention — purchase happens
  //    mid-year so Y1 is partial, the asset is held for N full years of
  //    operation, and the exit happens mid-year of the (N+1)th calendar
  //    period.  This is the convention used by Bonava / Consus / Instone
  //    exposé PDFs (and by the Amselweg PDF the user is reconciling
  //    against).  The PDF additionally accrues 1 extra month of loan
  //    interest in Y1 because financing starts before letting.
  //
  // Knobs:
  //   inp.firstYearMonths       — rent/opCosts/AfA months in Y1   (default 12)
  //   inp.loanStartOffsetMonths — extra Y1 loan months pre-letting (default 0)
  //   inp.pfandAsWerbungskosten — expense Pfandrecht in Y1 instead of
  //                               capitalising into knk (default false)
  // ──────────────────────────────────────────────────────────────────
  const N = Math.max(1, Math.floor(inp.holding));
  const fymRent = clamp(inp.firstYearMonths ?? 12, 1, 12);
  const lso = Math.max(0, inp.loanStartOffsetMonths ?? 0);
  const fymLoan = Math.min(12, fymRent + lso);
  const hasPartialYears = fymRent < 12;
  const periods = hasPartialYears ? N + 1 : N;
  // Months per period for rent / opCosts / AfA
  const rentMonths = Array.from({ length: periods }, (_, i) => {
    if (i === 0) return fymRent;
    if (hasPartialYears && i === periods - 1) return 12 - fymRent;
    return 12;
  });
  // Months per period for loan accrual. Y1 gets the extra "offset"
  // months (financing starts before letting). The EXIT period uses the
  // same months as the rent calendar — the loan ends when the asset
  // is sold, regardless of the Y1 offset (verified against PDF).
  const loanMonths = Array.from({ length: periods }, (_, i) => {
    if (i === 0) return fymLoan;
    if (hasPartialYears && i === periods - 1) return 12 - fymRent;
    return 12;
  });
  // Calendar-year index (0-based) used for rent-growth exponent — Y1=0, Y2=1, …
  const calIdx = (i) => i; // 0 → Y1, 1 → Y2, …, periods-1 → Y(N+1)

  const kpTotal = inp.kpApt + inp.kpFurn;
  const grunderwerbCost = inp.kpApt * inp.grunderwerb;
  const grundbuchKaufCost = inp.kpApt * inp.grundbuchKauf;
  const notarCost = inp.kpApt * inp.notar;

  const totalLoan = inp.l1Amount + inp.l2Amount;
  const pfandCost = totalLoan * inp.pfand;
  const knkTotal = grunderwerbCost + grundbuchKaufCost + notarCost + pfandCost;
  // When Pfandrecht is expensed in Y1 it is excluded from the AfA basis
  // (it's a financing cost, not an acquisition cost on the building).
  const knkNonFinancing = grunderwerbCost + grundbuchKaufCost + notarCost;

  const totalCost = kpTotal + knkTotal;
  const investment = inp.equity + knkTotal;

  const expectedLoan = kpTotal - inp.equity;
  const equityMismatch = Math.abs(totalLoan - expectedLoan) > 1
    ? { diff: totalLoan - expectedLoan, expected: expectedLoan }
    : null;

  // ──────────────────────────────────────────────────────────────────
  // Amortisation — German simple-proportional convention.
  // Per-period interest = balanceStart × rate × (months / 12).
  // Per-period annuity  = principal × (rate + tilg) × (months / 12).
  // Principal paid      = annuity − interest (clamped to remaining balance).
  // This matches the Amselweg PDF down to the euro (Y1 7-month accrual
  // gives interest 14,558 € exactly).
  // ──────────────────────────────────────────────────────────────────
  const l1 = amortizeProportional(inp.l1Amount, inp.l1Rate, inp.l1Tilg, loanMonths, 0);
  const l2 = amortizeProportional(inp.l2Amount, inp.l2Rate, inp.l2Tilg, loanMonths, Math.max(0, Math.floor(inp.l2Grace)));

  // Construction phase (MABV approximation)
  let constr = null;
  if (inp.constrMonths > 0 && totalLoan > 0) {
    const weightedRate = (inp.l1Amount * inp.l1Rate + inp.l2Amount * inp.l2Rate) / totalLoan;
    const avgDrawn = totalLoan * inp.constrDrawPct;
    const yearsConstr = inp.constrMonths / 12;
    const totalInterest = avgDrawn * weightedRate * yearsConstr;
    const taxSaving = totalInterest * inp.taxRate;
    const netCost = totalInterest - taxSaving;
    constr = {
      months: inp.constrMonths,
      weightedRate,
      avgDrawn,
      totalInterest,
      taxSaving,
      netCost,
      monthlyAvgInterest: totalInterest / inp.constrMonths,
      monthlyAvgNet: netCost / inp.constrMonths,
    };
  }

  const buildShare = 1 - inp.landShare;
  const buildingBasis = (inp.kpApt + knkNonFinancing) * buildShare;
  // Some developer PDFs (e.g. Amselweg 14B) wrap furniture into the
  // purchase price but do not depreciate it separately. Toggle via
  // `furnitureSeparateAfA: false` to suppress the furniture AfA stream.
  const furnitureBasisForAfA = (inp.furnitureSeparateAfA === false) ? 0 : inp.kpFurn;
  const depSched = buildDepreciationSchedule(
    buildingBasis,
    inp.sqm,
    inp.useDegressive,
    inp.useSonder7b,
    rentMonths, // pass per-period months so partial years get pro-rated AfA
    furnitureBasisForAfA,
    inp.depLinearRate ?? 0.03, // 2 % for existing-property buys (§7 Abs 4 Nr 2a EStG)
  );

  const rows = [];
  let cumNetCFAfterTax = 0;
  for (let i = 0; i < periods; i++) {
    const months = rentMonths[i];
    const yearFraction = months / 12;
    const rentAnnual = inp.rent * months * Math.pow(1 + inp.rentGrowth, calIdx(i)) * (1 - inp.vacancy);
    const opCosts = (inp.hausgeld + inp.verwalt) * months;
    const operatingCF = rentAnnual - opCosts;

    const l1Row = l1.schedule[i];
    const l2Row = l2.schedule[i];
    const interestTotal = l1Row.interest + l2Row.interest;
    const tilgungTotal = l1Row.principalPaid + l2Row.principalPaid;
    const debtServiceTotal = l1Row.payment + l2Row.payment;

    const cfDebtCharge = inp.interestOnlyCF ? interestTotal : debtServiceTotal;
    const netCFBeforeTax = operatingCF - cfDebtCharge;

    const dep = depSched[i];

    // Pfandrecht / Anfangskreditkosten as Werbungskosten — Y1 only.
    const pfandWerbung = inp.pfandAsWerbungskosten && i === 0 ? pfandCost : 0;

    const taxableIncome = rentAnnual - opCosts - interestTotal - dep.total - pfandWerbung;
    const taxEffect = -taxableIncome * inp.taxRate;
    const netCFAfterTax = netCFBeforeTax + taxEffect;
    cumNetCFAfterTax += netCFAfterTax;

    const balanceEnd = l1Row.balanceEnd + l2Row.balanceEnd;
    rows.push({
      year: i + 1,
      months,
      yearFraction,
      rent: rentAnnual,
      opCosts,
      operatingCF,
      interest: interestTotal,
      tilgung: tilgungTotal,
      debtService: debtServiceTotal,
      depTotal: dep.total,
      depDegressive: dep.degressive,
      depSonder7b: dep.sonder7b,
      depFurniture: dep.furniture,
      pfandWerbung,
      taxableIncome,
      taxEffect,
      netCFBeforeTax,
      netCFAfterTax,
      balanceEnd,
      cumNetCFAfterTax,
    });
  }

  // Sale value grows for N full holding years PLUS the partial exit year
  // (PDF convention: 10-year hold with half-year exit → 10.5 compounding
  // periods on kpApt). When firstYearMonths = 12, this collapses to N.
  const valueGrowthYears = N + (hasPartialYears ? (12 - fymRent) / 12 : 0);
  const valueEnd = inp.kpApt * Math.pow(1 + inp.valueGrowth, valueGrowthYears);
  const finalBalance = rows[rows.length - 1].balanceEnd;
  const saleProceedsAfterDebt = valueEnd - finalBalance;
  const wealth = saleProceedsAfterDebt + cumNetCFAfterTax;

  const irrCashflows = [-investment];
  for (let i = 0; i < periods; i++) {
    let cf = rows[i].netCFAfterTax;
    if (i === periods - 1) cf += saleProceedsAfterDebt;
    irrCashflows.push(cf);
  }
  const equityIRR = irr(irrCashflows, 0.1);

  const grossYield = (inp.rent * 12) / inp.kpApt;
  const netIncomeY1 = (inp.rent - inp.hausgeld - inp.verwalt) * 12;
  const netYield = netIncomeY1 / totalCost;
  const monthlyCFY1 = rows[0].netCFBeforeTax / Math.max(1, rows[0].months);
  const monthlyCFAfterTaxY1 = rows[0].netCFAfterTax / Math.max(1, rows[0].months);

  // German exposé-style "Eigenkapital-Verzinsung n. St." — CAGR on initial equity
  const ekFinalValue = cumNetCFAfterTax + saleProceedsAfterDebt;
  const ekVerzinsungExpose = investment > 0 && ekFinalValue > 0
    ? Math.pow(ekFinalValue / investment, 1 / valueGrowthYears) - 1
    : 0;

  return {
    inp,
    kpTotal,
    knkTotal,
    totalCost,
    investment,
    totalLoan,
    grunderwerbCost,
    grundbuchKaufCost,
    notarCost,
    pfandCost,
    rows,
    valueEnd,
    finalBalance,
    saleProceedsAfterDebt,
    wealth,
    equityIRR,
    grossYield,
    netYield,
    monthlyCFY1,
    monthlyCFAfterTaxY1,
    cumNetCFAfterTax,
    ekVerzinsungExpose,
    buildingBasis,
    constr,
    equityMismatch,
  };
}

// Default Off Market Karlshorst unit — WE08 profile (matches reference exposé screenshot)
// Values sourced from the 3 official exposé PDFs for Regener Str. 64
export const KARLS_DEFAULTS = {
  propName: "Off Market Karlshorst — WE08 · Regener Str. 64",
  propAddr: "Regener Str. 64, 10318 Berlin",
  rooms: 3,
  sqm: 62.78,
  kpApt: 506137, // apartment only — furniture added separately
  kpFurn: 7500,
  grunderwerb: 0.06,
  grundbuchKauf: 0.005,
  notar: 0.0135,
  pfand: 0.002,
  equity: 0,
  l1Amount: 363588, // bank loan (purchase price − KfW)
  l1Rate: 0.05,
  l1Tilg: 0.013,
  l2Amount: 150049, // KfW Klimafreundlicher Neubau mit QNG
  l2Rate: 0.026,
  l2Tilg: 0.0183, // KfW reference repayment
  l2Grace: 5,
  l2TilgungSubsidy: 0, // KfW Tilgungszuschuss (€) — applied at end of grace
  kfwEnabled: true,
  constrMonths: 43, // construction phase (months) to Q4 2028 handover
  constrDrawPct: 0.6,
  rent: 1440, // €21.03/m² × 62.78 + parking
  hausgeld: 85,
  verwalt: 30,
  valueGrowth: 0.03,
  rentGrowth: 0.02,
  vacancy: 0,
  holding: 10,
  taxRate: 0.42,
  landShare: 0.23,
  useDegressive: true,
  useSonder7b: true,
};

// ─── Predefined scenario presets — drives value growth / rent growth / vacancy
export const SCENARIOS = {
  individuell: { label: "Individuell", sub: "Custom assumptions" },
  konservativ: {
    label: "Konservativ",
    sub: "Cautious projection",
    overrides: { valueGrowth: 0.01, rentGrowth: 0.01, vacancy: 0.02 },
  },
  realistisch: {
    label: "Realistisch",
    sub: "Reference exposé base",
    overrides: { valueGrowth: 0.03, rentGrowth: 0.02, vacancy: 0 },
  },
  optimistisch: {
    label: "Optimistisch",
    sub: "Upside scenario",
    overrides: { valueGrowth: 0.04, rentGrowth: 0.03, vacancy: 0 },
  },
};

// Unit profiles — real apartments from the 3 official exposé PDFs (Regener Str. 64)
// WE01, WE08, WE13 — each with confirmed price, m², rooms, rent and financing structure
export const UNIT_PROFILES = {
  we01: {
    label: "WE01 — Regener Str. 64",
    sub: "2-room · 46.58 m² · €355,033 · QNG · A+",
    floor: "Ground floor",
    rooms: 2,
    sqm: 46.58,
    representative: "/assets/karls/floorplan-floor-hp.jpg",
    rendering: "/assets/karls/interior-living-room.jpg",
    overrides: {
      propName: "Off Market Karlshorst — WE01 · Regener Str. 64",
      rooms: 2,
      sqm: 46.58,
      kpApt: 355033,
      rent: 1025,
      hausgeld: 80,
      verwalt: 30,
      equity: 36253,
      l1Amount: 175443,
      l1Rate: 0.042,
      l1Tilg: 0.015,
      l2Amount: 150837,
      l2Rate: 0.025,
      l2Tilg: 0.0183,
      kfwEnabled: true,
    },
  },
  we08: {
    label: "WE08 — Regener Str. 64",
    sub: "3-room · 62.78 m² · €506,137 + parking · QNG · A+",
    floor: "Mid floor",
    rooms: 3,
    sqm: 62.78,
    representative: "/assets/karls/floorplan-unit-we3208.jpg",
    rendering: "/assets/karls/interior-kitchen.jpg",
    overrides: {
      propName: "Off Market Karlshorst — WE08 · Regener Str. 64",
      rooms: 3,
      sqm: 62.78,
      kpApt: 506137,
      rent: 1440,
      hausgeld: 85,
      verwalt: 30,
      equity: 0,
      l1Amount: 363588,
      l1Rate: 0.05,
      l1Tilg: 0.013,
      l2Amount: 150049,
      l2Rate: 0.026,
      l2Tilg: 0.0183,
      kfwEnabled: true,
    },
  },
  we13: {
    label: "WE13 — Regener Str. 64",
    sub: "2-room · 59.86 m² · €456,223 · QNG · A+",
    floor: "Top floor",
    rooms: 2,
    sqm: 59.86,
    representative: "/assets/karls/floorplan-floor-dg.jpg",
    rendering: "/assets/karls/interior-bedroom.jpg",
    overrides: {
      propName: "Off Market Karlshorst — WE13 · Regener Str. 64",
      rooms: 2,
      sqm: 59.86,
      kpApt: 456223,
      rent: 1320,
      hausgeld: 85,
      verwalt: 30,
      equity: 18549,
      l1Amount: 295174,
      l1Rate: 0.04,
      l1Tilg: 0.01,
      l2Amount: 150000,
      l2Rate: 0.0254,
      l2Tilg: 0.0183,
      l2Grace: 0, // no grace — KfW repays at 1.83% from Year 1 (no Tilgungszuschuss applied)
      pfand: 0.0013, // exposé shows 0.13% for WE13 (not standard 0.2%)
      kfwEnabled: true,
    },
  },
  custom: {
    label: "Custom",
    sub: "Enter your own numbers",
    floor: null,
    rooms: null,
    sqm: null,
    representative: null,
    rendering: null,
    overrides: {},
  },
};

